import * as React from "react"
import { SafeImage } from "@/components/ui/safe-image"
import { accentColorFor } from "./types"

interface BrandLogoProps {
  name: string
  slug: string
  logoPath?: string | null
  /** Outer tile size in px. Default 52 (handoff list-card spec). */
  size?: number
  /** Override rounded corners. Default rounded-[10px] (handoff §6). */
  className?: string
}

/**
 * Initials fallback derivation — matches the handoff `initials()` in
 * `brands-components.jsx`:
 *   - Strips parenthetical sub-brand suffix
 *   - Splits on whitespace/&/+/-/'
 *   - 1 word → first letter; ≥2 words → first letter of first two words
 */
function deriveInitials(name: string): string {
  const cleaned = (name || "").replace(/\(.*?\)/g, "").trim()
  const words = cleaned.split(/[\s&+\-']/).filter(Boolean)
  if (words.length === 0) return "?"
  if (words.length === 1) return words[0].charAt(0).toUpperCase()
  return (words[0].charAt(0) + (words[1] || "").charAt(0)).toUpperCase()
}

/**
 * BrandLogo — renders either:
 *   (a) the real brand logo from `logo_path` (most common when available), or
 *   (b) a tinted square tile with the brand's initial(s) in a deterministic
 *       accent color, per handoff §6.
 *
 * Border / radius matches the handoff: white bg + gray-200 border when there's
 * a real logo, transparent border + tinted accent bg for the fallback.
 */
export function BrandLogo({
  name,
  slug,
  logoPath,
  size = 52,
  className = "",
}: BrandLogoProps) {
  const hasReal = !!logoPath && logoPath.length > 0
  const accent = accentColorFor(slug)

  if (hasReal) {
    return (
      <div
        className={`flex flex-shrink-0 items-center justify-center overflow-hidden rounded-[10px] border border-gray-200 bg-white ${className}`}
        style={{ width: size, height: size }}
      >
        <SafeImage
          src={logoPath!}
          alt={`${name} logo`}
          width={Math.round(size * 0.78)}
          height={Math.round(size * 0.78)}
          className="object-contain"
        />
      </div>
    )
  }

  return (
    <div
      className={`flex flex-shrink-0 items-center justify-center rounded-[10px] border border-transparent ${className}`}
      style={{
        width: size,
        height: size,
        background: `${accent}14`, // ~8% tint
      }}
      aria-hidden="true"
    >
      <span
        style={{
          color: accent,
          fontSize: Math.round(size * 0.38),
          fontWeight: 900,
          letterSpacing: "-0.02em",
        }}
      >
        {deriveInitials(name)}
      </span>
    </div>
  )
}
